/**
 * Token Refresh Service — Single Source of Truth for Admin Panel Auth
 *
 * WHY THIS EXISTS:
 * Without centralized refresh coordination, multiple Axios interceptors and
 * React-Admin's authProvider would each independently detect token expiration
 * and fire parallel refresh requests. Cognito rotates refresh tokens on use,
 * so the second parallel refresh would arrive with an already-consumed token
 * and fail with "Invalid Refresh Token", logging the user out.
 *
 * HOW IT PREVENTS THAT:
 * A singleton boolean (`isRefreshing`) gates entry into the refresh flow.
 * The FIRST caller sets `isRefreshing = true` synchronously (no await between
 * the check and the set, so no interleaving is possible in single-threaded JS),
 * then performs the actual refresh. All subsequent callers during that window
 * register {resolve, reject} subscriber pairs and await a promise that resolves
 * when the first caller's refresh completes or rejects if it fails.
 *
 * WHY SUBSCRIBERS HAVE BOTH resolve AND reject:
 * Previously subscribers only had a resolve callback. If the refresh failed,
 * `clearSubscribers()` silently dropped all pending promises, causing callers
 * to hang indefinitely — they never resolved OR rejected. Now `onRefreshFailed()`
 * properly rejects all waiting promises so callers can handle the failure.
 *
 * ENVIRONMENT ISOLATION:
 * Dev and Staging share a single Cognito User Pool (`eu-west-1_oZPYiNcSK`)
 * but run on different origins (localhost vs staging subdomain). localStorage
 * is origin-scoped by the browser's same-origin policy, so tokens from one
 * environment CANNOT leak into another — no cross-contamination is possible.
 *
 * DEVICE KEY:
 * Cognito device tracking is enabled in the pool but confirmDevice() is never
 * called after sign-in. Passing unconfirmed device keys during REFRESH_TOKEN_AUTH
 * causes "Invalid device credentials" errors. We intentionally omit deviceKey
 * from all refresh requests until SRP-based device confirmation is implemented.
 */

import { jwtDecode } from 'jwt-decode';
import * as Sentry from '@sentry/react';
import Logger from '../utils/Logger';
import config from '../config';

interface DecodedToken {
  exp: number;
  name?: string;
  username?: string;
  'cognito:username'?: string;
  sub?: string;
  [key: string]: any;
}

interface RefreshResult {
  success: boolean;
  idToken?: string;
  accessToken?: string;
  /** When true, the refresh token is permanently invalid (401/403 from Cognito).
   *  Callers should clear tokens and redirect to login — retrying won't help. */
  permanent?: boolean;
}

// ============================================================================
// SINGLETON REFRESH GATE
// ============================================================================
// Threading safety: JavaScript is single-threaded. The `if (isRefreshing)`
// check and `isRefreshing = true` assignment in refreshToken() execute in
// the SAME synchronous microtask — no `await` between them — so no
// interleaving is possible. This is NOT a mutex; it's a simple boolean gate
// that works because JS never preempts synchronous code.
//
// Thundering Herd (10 parallel 401s at the same instant):
//   1. All 10 response interceptors call refreshToken() synchronously
//   2. The FIRST call sees isRefreshing=false, sets it to true, starts refresh
//   3. Calls 2-10 see isRefreshing=true, push subscriber pairs, and await
//   4. When refresh completes, onRefreshComplete() resolves all 9 subscribers
//   5. All 10 requests retry with the new token
//   Result: Exactly 1 Cognito API call, 0 race conditions
// ============================================================================
let isRefreshing = false;
let isLoggedOut = false; // Guards against zombie token writes after logout
let refreshSubscribers: { resolve: (token: string) => void; reject: (error: Error) => void }[] = [];

// Buffer time in seconds (5 minutes before expiration)
const TOKEN_REFRESH_BUFFER_SECONDS = 300;

/**
 * Add a subscriber to be notified when token refresh completes or fails.
 * FIX: Subscribers now have both resolve and reject callbacks so they don't
 * hang forever when refresh fails.
 */
const addSubscriber = (resolve: (token: string) => void, reject: (error: Error) => void): void => {
  refreshSubscribers.push({ resolve, reject });
};

/**
 * Notify all subscribers that token refresh completed
 */
const onRefreshComplete = (newToken: string): void => {
  refreshSubscribers.forEach(({ resolve }) => resolve(newToken));
  refreshSubscribers = [];
};

/**
 * Reject all subscribers on refresh failure.
 * FIX: Previously called clearSubscribers() which silently dropped promises,
 * causing callers to hang indefinitely. Now properly rejects them.
 */
const onRefreshFailed = (error: Error): void => {
  refreshSubscribers.forEach(({ reject }) => reject(error));
  refreshSubscribers = [];
};

/**
 * Get the current token from localStorage
 */
export const getStoredToken = (): string | null => {
  return localStorage.getItem('token');
};

/**
 * Get the refresh token from localStorage
 */
export const getStoredRefreshToken = (): string | null => {
  return localStorage.getItem('refreshToken');
};

/**
 * Get the device key from localStorage
 * Used by Cognito for per-device token isolation
 */
export const getStoredDeviceKey = (): string | null => {
  return localStorage.getItem('deviceKey');
};

/**
 * Decode a JWT token safely
 */
export const decodeToken = (token: string): DecodedToken | null => {
  try {
    return jwtDecode<DecodedToken>(token);
  } catch (error) {
    Logger.error('[TokenRefreshService] Failed to decode token:', error);
    return null;
  }
};

/**
 * Check if a token is expired or will expire soon
 * Returns true if token should be refreshed
 */
export const shouldRefreshToken = (token: string): boolean => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return true; // Invalid token, should refresh
  }

  const currentTime = Date.now() / 1000;
  const expirationTime = decoded.exp;
  const timeUntilExpiry = expirationTime - currentTime;

  // Refresh if less than 5 minutes until expiry
  return timeUntilExpiry < TOKEN_REFRESH_BUFFER_SECONDS;
};

/**
 * Check if a token is completely expired (past expiration time)
 */
export const isTokenExpired = (token: string): boolean => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return true;
  }

  const currentTime = Date.now() / 1000;
  return decoded.exp < currentTime;
};

/**
 * The Cognito USERNAME the backend needs for a refresh.
 *
 * The backend computes Cognito's SECRET_HASH as HMAC(username + clientId), and
 * Cognito only accepts it when `username` is the user's Cognito username — the
 * one the mobile app reads as `username` off the ACCESS token. The admin stores
 * the ID token, where the same value is the `cognito:username` claim. The `sub`
 * is a different UUID (this pool's usernames are not the sub), and a display
 * `name` is never a username — either produced "Unable to verify secret hash"
 * on every refresh and a forced re-login each hour.
 */
export const getUserIdFromToken = (token: string): string | null => {
  const decoded = decodeToken(token);
  if (!decoded) return null;

  return decoded['cognito:username'] || decoded.username || null;
};

/**
 * Check if a token is an OAuth token (vs Cognito token)
 * OAuth tokens have issuer "deeliver-oauth"
 */
const isOAuthToken = (token: string): boolean => {
  const decoded = decodeToken(token);
  return decoded?.iss === 'deeliver-oauth';
};

/**
 * Perform OAuth token refresh
 */
const performOAuthRefresh = async (userId: string, storedRefreshToken: string): Promise<RefreshResult> => {
  try {
    const response = await fetch(`${config.API_URL}/auth/oauth/refresh`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refreshToken: storedRefreshToken,
        userId: userId,
      }),
    });

    if (!response.ok) {
      Logger.error('[TokenRefreshService] OAuth refresh failed with status:', response.status);
      return { success: false };
    }

    const data = await response.json();

    if (data.idToken) {
      if (isLoggedOut) return { success: false }; // Don't write tokens after logout
      localStorage.setItem('token', data.idToken);
      if (data.accessToken) {
        localStorage.setItem('accessToken', data.accessToken);
      }

      Logger.info('[TokenRefreshService] OAuth token refreshed successfully');
      return {
        success: true,
        idToken: data.idToken,
        accessToken: data.accessToken,
      };
    }

    return { success: false };
  } catch (error) {
    Logger.error('[TokenRefreshService] OAuth refresh error:', error);
    return { success: false };
  }
};

/**
 * Perform Cognito token refresh
 */
const performCognitoRefresh = async (userId: string, storedRefreshToken: string): Promise<RefreshResult> => {
  try {
    // FIX: Do not send deviceKey — device confirmation is not implemented in Cognito,
    // so unconfirmed device keys cause "Invalid device credentials" errors on refresh.
    const response = await fetch(`${config.API_URL}/auth/refresh-token`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'x-refresh-token': storedRefreshToken,
      },
      body: JSON.stringify({ id: userId }),
    });

    if (!response.ok) {
      Logger.error('[TokenRefreshService] Cognito refresh failed with status:', response.status);
      // 401/403 = Cognito permanently rejected the refresh token (expired/revoked)
      // Other errors (500, network) are transient and worth retrying
      const permanent = response.status === 401 || response.status === 403;
      return { success: false, permanent };
    }

    const data = await response.json();

    if (data.idToken) {
      if (isLoggedOut) return { success: false }; // Don't write tokens after logout
      localStorage.setItem('token', data.idToken);
      if (data.accessToken) {
        localStorage.setItem('accessToken', data.accessToken);
      }

      Logger.info('[TokenRefreshService] Cognito token refreshed successfully');
      return {
        success: true,
        idToken: data.idToken,
        accessToken: data.accessToken,
      };
    }

    return { success: false };
  } catch (error) {
    Logger.error('[TokenRefreshService] Cognito refresh error:', error);
    return { success: false };
  }
};

/**
 * Perform the actual token refresh API call with retry for transient failures.
 * Automatically detects OAuth vs Cognito tokens and uses appropriate endpoint.
 *
 * WHY AUTO-DETECT:
 * Staff users may authenticate via Cognito (email/password) or OAuth (SSO).
 * Each uses a different refresh endpoint and request format. Detecting the
 * token type here keeps the caller (refreshToken) agnostic to auth method.
 *
 * WHY RETRY:
 * Transient failures (network blip, 502/503 from ALB) should not log the user
 * out. We retry once after 1s. Auth failures (401/403/400) indicate the refresh
 * token itself is invalid/revoked and are NOT retried — those trigger logout.
 */
const performTokenRefresh = async (): Promise<RefreshResult> => {
  const token = getStoredToken();
  const storedRefreshToken = getStoredRefreshToken();

  if (!token || !storedRefreshToken) {
    return { success: false };
  }

  const userId = getUserIdFromToken(token);
  if (!userId) {
    Logger.error('[TokenRefreshService] Could not extract user ID from token');
    return { success: false };
  }

  const doRefresh = (): Promise<RefreshResult> => {
    if (isOAuthToken(token)) {
      return performOAuthRefresh(userId, storedRefreshToken);
    } else {
      return performCognitoRefresh(userId, storedRefreshToken);
    }
  };

  const tokenType = isOAuthToken(token) ? 'OAuth' : 'Cognito';
  Logger.info(`[TokenRefreshService] Detected ${tokenType} token, using ${tokenType} refresh`);

  const result = await doRefresh();
  if (result.success) {
    return result;
  }

  // Don't retry if Cognito permanently rejected the refresh token
  if (result.permanent) {
    Logger.error('[TokenRefreshService] Refresh token permanently rejected by Cognito — clearing session');
    clearTokens();
    return result;
  }

  // Retry once after 1s for transient failures
  Logger.warn('[TokenRefreshService] First refresh attempt failed, retrying in 1s...');
  await new Promise((resolve) => setTimeout(resolve, 1000));
  return doRefresh();
};

/**
 * Refresh the token with singleton protection.
 *
 * WHY SINGLETON:
 * Cognito may rotate refresh tokens on each use (depending on pool config).
 * If two callers refresh simultaneously, the second uses an already-consumed
 * refresh token and gets "Invalid Refresh Token", triggering an unnecessary
 * logout. The singleton ensures exactly ONE Cognito call per refresh cycle.
 *
 * WHY isRefreshing IS SET BEFORE await:
 * `isRefreshing = true` is set synchronously in the same microtask as the
 * `if (isRefreshing)` check. There is no await/yield between them, so JS's
 * single-threaded event loop guarantees atomicity. This is intentional.
 *
 * @returns The new idToken string, or null if refresh failed (tokens cleared)
 */
export const refreshToken = async (): Promise<string | null> => {
  // If logged out, don't attempt refresh (prevents zombie token writes)
  if (isLoggedOut) {
    return null;
  }

  // If already refreshing, wait for the current refresh to complete
  if (isRefreshing) {
    return new Promise<string | null>((resolve, reject) => {
      addSubscriber(
        (newToken: string) => resolve(newToken),
        (error: Error) => reject(error),
      );
    });
  }

  isRefreshing = true;
  const refreshStart = Date.now();

  try {
    const result = await performTokenRefresh();

    if (result.success && result.idToken) {
      const durationMs = Date.now() - refreshStart;
      if (typeof Sentry.metrics?.distribution === 'function') {
        Sentry.metrics.distribution('admin.auth.token_refresh_time', durationMs, { unit: 'millisecond' });
        Sentry.metrics.count('admin.auth.token_refresh', 1, { attributes: { result: 'success' } });
        Sentry.metrics.gauge('admin.auth.refresh_subscribers', refreshSubscribers.length);
      }
      onRefreshComplete(result.idToken);
      return result.idToken;
    }

    if (typeof Sentry.metrics?.count === 'function') {
      Sentry.metrics.count('admin.auth.token_refresh', 1, { attributes: { result: 'failure' } });
    }
    Logger.warn('[TokenRefreshService] Refresh failed, returning null');
    const error = new Error('Token refresh failed');
    onRefreshFailed(error);
    return null;
  } catch (err) {
    if (typeof Sentry.metrics?.count === 'function') {
      Sentry.metrics.count('admin.auth.token_refresh', 1, { attributes: { result: 'error' } });
    }
    Logger.warn('[TokenRefreshService] Refresh error:', err);
    const error = err instanceof Error ? err : new Error('Token refresh error');
    onRefreshFailed(error);
    return null;
  } finally {
    isRefreshing = false;
  }
};

/**
 * Clear all stored tokens and session data (logout)
 */
export const clearTokens = (): void => {
  isLoggedOut = true; // Prevent any in-flight refresh from writing tokens back
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('deviceKey');
  localStorage.removeItem('userRoles');
  localStorage.removeItem('userPagePermissions');
};

/**
 * Get a valid token, refreshing if necessary
 * This is the main entry point for getting a token before making API requests
 *
 * @returns Promise<string | null> - A valid token or null if not available
 */
export const getValidToken = async (): Promise<string | null> => {
  const token = getStoredToken();

  if (!token) {
    return null;
  }

  // Check if token needs refresh
  if (shouldRefreshToken(token)) {
    Logger.info('[TokenRefreshService] Token needs refresh (expires soon or expired)');
    return refreshToken();
  }

  return token;
};

/**
 * Check if we are currently in the process of refreshing
 */
export const isCurrentlyRefreshing = (): boolean => {
  return isRefreshing;
};

// ============================================================================
// SCHEDULED TOKEN REFRESH — Standard pattern used by Auth0, MSAL, Amplify
// ============================================================================
// HOW IT WORKS:
// After login or successful refresh, we decode the new token's `exp` claim
// and schedule a single setTimeout to fire exactly when the token needs
// refreshing (exp - buffer). No polling, no wasted checks — one precise timer.
//
// On success the new token is stored and a new timeout is scheduled for it,
// creating an indefinite refresh chain that keeps the session alive.
//
// VISIBILITY CHANGE:
// When the user switches back to the admin tab after being away, the token
// may already be expired (laptop sleep, long background). The visibilitychange
// listener does an immediate check and refreshes if needed, then reschedules.
// ============================================================================
let refreshTimeoutId: ReturnType<typeof setTimeout> | null = null;
let visibilityListenerAttached = false;

const handleVisibilityChange = (): void => {
  if (document.visibilityState === 'visible') {
    const token = getStoredToken();
    if (!token) return;

    if (isTokenExpired(token) || shouldRefreshToken(token)) {
      Logger.info('[TokenRefreshService] Tab visible + token expiring/expired, refreshing...');
      refreshToken()
        .then(() => scheduleRefresh())
        .catch(() => {
          // Don't clear tokens here — let checkAuth handle it on next navigation.
          // Transient failures (laptop wake, network reconnecting) should not log user out.
          Logger.warn('[TokenRefreshService] Visibility refresh failed, will retry on next check');
        });
    } else {
      // Token still valid — reschedule in case setTimeout drifted during sleep
      scheduleRefresh();
    }
  }
};

/**
 * Schedule a single timeout to refresh the token at exactly the right moment.
 * Reads the current token's `exp`, subtracts the buffer, and sets one timer.
 * Safe to call multiple times — cancels any existing timer first.
 */
const scheduleRefresh = (): void => {
  if (refreshTimeoutId) {
    clearTimeout(refreshTimeoutId);
    refreshTimeoutId = null;
  }

  const token = getStoredToken();
  if (!token) return;

  const decoded = decodeToken(token);
  if (!decoded?.exp) return;

  const nowSeconds = Date.now() / 1000;
  const refreshAtSeconds = decoded.exp - TOKEN_REFRESH_BUFFER_SECONDS;
  const delayMs = Math.max((refreshAtSeconds - nowSeconds) * 1000, 0);

  Logger.info(
    `[TokenRefreshService] Token refresh scheduled in ${Math.round(delayMs / 1000 / 60)}min ${Math.round((delayMs / 1000) % 60)}s`
  );

  refreshTimeoutId = setTimeout(async () => {
    refreshTimeoutId = null;
    const token = getStoredToken();
    if (!token) return;

    Logger.info('[TokenRefreshService] Scheduled refresh firing');
    try {
      const newToken = await refreshToken();
      if (newToken) {
        // Chain: schedule the next refresh for the new token
        scheduleRefresh();
      }
    } catch {
      // Refresh failed — interceptors or checkAuth will handle logout
    }
  }, delayMs);
};

/**
 * Start the scheduled token refresh and visibility listener.
 * Called after login and on checkAuth (page reload).
 * Safe to call multiple times — reschedules based on current token.
 * Deduplicates the visibilitychange listener (previously each checkAuth
 * call added another listener, causing N duplicate refreshes on tab focus).
 */
export const startBackgroundRefresh = (): void => {
  isLoggedOut = false; // Reset logout guard (called after login)
  scheduleRefresh();
  if (!visibilityListenerAttached) {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    visibilityListenerAttached = true;
  }
};

/**
 * Stop the scheduled refresh and visibility listener.
 * Called on logout.
 */
export const stopBackgroundRefresh = (): void => {
  if (refreshTimeoutId) {
    clearTimeout(refreshTimeoutId);
    refreshTimeoutId = null;
  }
  if (visibilityListenerAttached) {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    visibilityListenerAttached = false;
  }
};

/**
 * Wait for the current refresh to complete (if any)
 * Useful for components that need to wait for a valid token
 */
export const waitForRefresh = (): Promise<string | null> => {
  if (!isRefreshing) {
    return Promise.resolve(getStoredToken());
  }

  return new Promise<string | null>((resolve, reject) => {
    addSubscriber(
      (newToken: string) => resolve(newToken),
      () => resolve(null), // On failure, resolve with null (don't reject — callers handle null gracefully)
    );
  });
};

export default {
  getStoredToken,
  getStoredRefreshToken,
  decodeToken,
  shouldRefreshToken,
  isTokenExpired,
  refreshToken,
  clearTokens,
  getValidToken,
  isCurrentlyRefreshing,
  waitForRefresh,
  startBackgroundRefresh,
  stopBackgroundRefresh,
};
